import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ExternalLink, Play } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { api } from "../lib/api";
import { useMediaPlayer, classifyMedia } from "../lib/player";

const META = {
    podcasts: { type: "podcast", title: "Podcasts", subtitle: "Real talks. Real people. Real stories." },
    playlists: { type: "playlist", title: "Playlists", subtitle: "Curated hits, updated every week." },
    interviews: { type: "interview", title: "Interviews", subtitle: "Inside conversations with the culture." },
    videos: { type: "video", title: "Videos", subtitle: "The hottest visuals in one place." },
    events: { type: "event", title: "Events", subtitle: "Parties, shows and major events." },
};

export default function MediaListPage() {
    const { kind } = useParams();
    const meta = META[kind];
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const { playItem, current, playing } = useMediaPlayer();

    useEffect(() => {
        if (!meta) return;
        setLoading(true);
        api.get("/media", { params: { type: meta.type, limit: 50 } })
            .then((r) => setItems(r.data))
            .finally(() => setLoading(false));
    }, [meta]);

    if (!meta) {
        return (
            <div className="bg-black text-white min-h-screen">
                <Header />
                <main className="max-w-3xl mx-auto px-4 md:px-8 py-20 text-streetz-muted">Unknown section.</main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="bg-black text-white min-h-screen">
            <SEO title={meta.title} description={meta.subtitle} keywords={[meta.type, "Afrobeats", "MyStreetzMag"]} />
            <Header />
            <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-12">
                <div className="overline text-streetz-yellow">{meta.type.toUpperCase()}</div>
                <h1 className="font-display uppercase text-5xl md:text-7xl leading-none mt-2" data-testid={`media-page-title-${kind}`}>{meta.title}</h1>
                <p className="text-streetz-muted mt-2">{meta.subtitle}</p>

                {loading ? (
                    <p className="text-streetz-muted mt-12">Loading…</p>
                ) : items.length === 0 ? (
                    <p className="text-streetz-muted mt-12">Coming soon.</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10" data-testid={`media-grid-${kind}`}>
                        {items.map((m) => {
                            const playable = ["audio", "spotify"].includes(classifyMedia(m.media_url));
                            const isActive = current?.id === m.id;
                            const handleClick = (e) => {
                                if (playable) {
                                    e.preventDefault();
                                    const queueable = items
                                        .filter((x) => ["audio", "spotify"].includes(classifyMedia(x.media_url)))
                                        .map((x) => ({ id: x.id, title: x.title, host: x.host, image: x.image_url, url: x.media_url }));
                                    playItem(
                                        { id: m.id, title: m.title, host: m.host, image: m.image_url, url: m.media_url },
                                        queueable
                                    );
                                }
                            };
                            return (
                                <a
                                    key={m.id}
                                    href={m.media_url || "#"}
                                    target={playable ? undefined : "_blank"}
                                    rel="noopener noreferrer"
                                    onClick={handleClick}
                                    data-testid={`media-item-${m.slug}`}
                                    className={`group bg-streetz-paper border transition-colors flex flex-col ${
                                        isActive ? "border-streetz-yellow" : "border-streetz-line hover:border-streetz-yellow"
                                    }`}
                                >
                                    <div className="relative">
                                        {m.image_url && (
                                            <img src={m.image_url} alt={m.title} className="aspect-video object-cover w-full" />
                                        )}
                                        {playable && (
                                            <div
                                                data-testid={`media-play-${m.slug}`}
                                                className="absolute bottom-3 right-3 h-12 w-12 grid place-items-center bg-streetz-red text-white rounded-full shadow-lg group-hover:bg-streetz-yellow group-hover:text-black transition-colors"
                                                aria-label="Play"
                                            >
                                                <Play className="w-5 h-5 ml-[2px]" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-5 flex-1 flex flex-col">
                                        <span className="overline text-streetz-red">{m.host || meta.type}</span>
                                        <h3 className="font-display uppercase text-xl mt-2 leading-tight group-hover:text-streetz-yellow">{m.title}</h3>
                                        <p className="text-sm text-streetz-muted mt-2 line-clamp-2 flex-1">{m.description}</p>
                                        <div className="flex items-center justify-between mt-4">
                                            <span className="overline text-streetz-muted">{m.duration}</span>
                                            <span className="overline text-white inline-flex items-center gap-1 group-hover:text-streetz-yellow">
                                                {playable ? (isActive && playing ? "PLAYING" : "PLAY") : "OPEN"}
                                                {playable ? <Play className="w-3 h-3" /> : <ExternalLink className="w-3 h-3" />}
                                            </span>
                                        </div>
                                    </div>
                                </a>
                            );
                        })}
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
}
